import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { cartMessageText } from '../../../helpers/shop/cartMessages';

const money = value => Number(value || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
const origin = row => row.account_quantity && row.guest_quantity ? 'Cantidades combinadas'
    : row.guest_quantity ? 'Agregado como invitado' : 'Ya estaba en tu cuenta';
const notice = message => ({
    'btl-no-eligible-products': 'El código médico se retiró porque no hay productos participantes.',
    'btl-shipping-method-missing': 'No hay un método de envío compatible con el código médico.',
    'btl-coupon-inactive': 'El código médico ya no está activo.',
    'btl-coupon-expired': 'El código médico ya no está vigente.'
}[message] || (message.startsWith('btl-')
    ? 'El código médico no está disponible para este carrito.'
    : cartMessageText(message)));

const ProductLabel = ({ row }) => <Stack spacing={0.5} alignItems="flex-start">
    <Typography fontWeight={600}>{row.title}</Typography>
    {row.variant_title && <Typography variant="body2" color="text.secondary">{row.variant_title}</Typography>}
    <Chip size="small" variant="outlined" color={row.guest_quantity ? 'primary' : 'default'} label={origin(row)} />
    {row.reason && <Typography variant="body2" color="warning.dark">
        Solicitaste {row.account_quantity + row.guest_quantity}; quedaron {row.final_quantity}. {row.reason}.
    </Typography>}
</Stack>;

export const CartMergeReviewDialog = ({ review, cart, shipping, onContinue, onReview }) => {
    const canContinue = cart.item.length > 0 && Boolean(shipping?._id) && !cart.requires_cart_review;
    return <Dialog open disableEscapeKeyDown fullWidth maxWidth="md" aria-labelledby="cart-merge-title">
        <DialogTitle id="cart-merge-title">{review.merged ? 'Unimos tus carritos' : 'Actualizamos tu carrito'}</DialogTitle>
        <DialogContent dividers>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
                {review.merged ? 'Recuperamos los productos de tu cuenta e incorporamos los que agregaste antes de iniciar sesión.'
                    : 'Revisamos la disponibilidad y las condiciones actuales de tus productos.'}
                {' '}Revisa las cantidades y el total antes de continuar.
            </Typography>
            <TableContainer sx={{ display: { xs: 'none', sm: 'block' }, mb: 3 }}>
                <Table size="small" aria-label="Detalle de la unión de carritos">
                    <TableHead><TableRow>
                        <TableCell>Producto / variante</TableCell><TableCell align="center">En tu cuenta</TableCell>
                        <TableCell align="center">Como invitado</TableCell><TableCell align="center">Cantidad final</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{review.rows.map(row => <TableRow key={`${row.prod_id}:${row.prod_var_id}`}>
                        <TableCell sx={{ py: 2 }}><ProductLabel row={row} /></TableCell>
                        <TableCell align="center">{row.account_quantity}</TableCell>
                        <TableCell align="center">{row.guest_quantity}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{row.final_quantity}</TableCell>
                    </TableRow>)}</TableBody>
                </Table>
            </TableContainer>
            <Stack spacing={2} sx={{ display: { xs: 'flex', sm: 'none' }, mb: 3 }}>
                {review.rows.map(row => <Box key={`${row.prod_id}:${row.prod_var_id}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
                    <ProductLabel row={row} />
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        {[['En tu cuenta', row.account_quantity], ['Como invitado', row.guest_quantity], ['Cantidad final', row.final_quantity]].map(([label, value]) =>
                            <Box textAlign="center" key={label}><Typography variant="caption">{label}</Typography><Typography fontWeight={600}>{value}</Typography></Box>)}
                    </Stack>
                </Box>)}
            </Stack>
            <Stack spacing={1} sx={{ mb: 2 }}>
                {(cart.cart_msg || []).map((message, index) => <Alert severity="warning" key={index}>{notice(message)}</Alert>)}
                {!cart.item.length && <Alert severity="warning">No quedaron productos disponibles. Revisa tu carrito para continuar.</Alert>}
                {cart.requires_cart_review && <Alert severity="info">El código necesita tu confirmación o correo de compra. Selecciona “Revisar carrito” para terminar de aplicarlo.</Alert>}
                {!shipping?._id && <Alert severity="info">Falta seleccionar un método de envío válido. Puedes hacerlo en “Revisar carrito”.</Alert>}
            </Stack>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2 }}>
                <Typography fontWeight={600} sx={{ mb: 1 }}>Tu resumen actualizado</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{cart.coupon?.name
                    ? `${cart.coupon.source === 'btl' ? 'Código médico' : 'Cupón'}: ${cart.coupon.name}` : 'Sin cupón aplicado'}</Typography>
                {[['Descuento por promociones', cart.summary.promos_discount], ['Descuento por cupón', cart.summary.discount]].map(([label, amount]) =>
                    <Stack key={label} direction="row" justifyContent="space-between"><Typography variant="body2">{label}</Typography><Typography variant="body2">−{money(amount)}</Typography></Stack>)}
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Envío</Typography><Typography variant="body2">{shipping?._id ? money(shipping.cost) : 'Por seleccionar'}</Typography></Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between"><Typography fontWeight={700}>{shipping?._id ? 'Total' : 'Subtotal sin envío'}</Typography><Typography fontWeight={700}>{money(cart.summary.total)}</Typography></Stack>
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={onReview}>Revisar carrito</Button>
            <Button variant="contained" disabled={!canContinue} onClick={onContinue}>Continuar al checkout</Button>
        </DialogActions>
    </Dialog>;
};
